/**
 * Projected whole-project margin for fixed-price work.
 *
 * On a fixed-price project the closed-month margin swings with when milestones
 * happen to invoice; the question that matters is what the whole contract
 * earns. The engine answers it: contract value is the NOS billing schedule
 * (By Period fees counted to their end date), remaining cost is planned hours
 * on open months priced per person, as the engine's `cost_rate_basis` says. Every figure here is the engine's -- this file formats and
 * labels, it does not compute.
 *
 * It is a PROJECTION and is styled like the scheduled-billing card beside it:
 * dashed, badged, never mixed into the closed-period figures. It always sits
 * beside the actual-only margin so the reader can see how much of the answer
 * rests on the rate assumption. When planned hours leave months uncovered the
 * figure is optimistic, and a small amber "Partial plan" flag sits right beside
 * it -- quiet enough to live with, never absent. The flag carries no text of
 * its own: the reasons are printed once, as muted lines under the figures (the
 * engine's warnings, as on the This month card). Exclusions are not warnings:
 * they replace the number, so they stay inline.
 */
import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { calendarDay, hours, money, optionalMoney, percent, rate } from "@/lib/format";
import type {
  AccountProjectedMargin,
  AccountProjectedMarginComputed,
  ProjectedMarginComputed,
  ProjectedMarginFigures,
  ProjectProjectedMargin,
} from "@/lib/profitability-api";

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="w-full border-dashed bg-muted/20">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {title}
          <Badge variant="outline" className="border-dashed font-medium">
            Projected
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

/** The at-a-glance flag. The reasons are the muted lines below, said once. */
function PartialPlan() {
  return (
    <span
      data-testid="partial-plan"
      title="Some of the projection is missing; see the notes below the figures."
      className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400"
    >
      <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
      Partial plan
    </span>
  );
}

function Headline({ figures, hint }: { figures: ProjectedMarginFigures; hint?: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div data-projected-figure>
        <p className="text-xs text-muted-foreground">Projected margin</p>
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-semibold tabular-nums">{percent(figures.margin_pct)}</span>
          {hint}
        </p>
        {/* Dollars are absent for Aggregate users: margin percent only. */}
        {figures.margin === undefined ? null : (
          <p className="text-sm tabular-nums text-muted-foreground">{money(figures.margin)}</p>
        )}
      </div>
      <div>
        <p
          className="text-xs text-muted-foreground"
          title="The same contract value against the cost booked so far, with nothing projected"
        >
          Actual cost only
        </p>
        <p className="text-2xl font-semibold tabular-nums text-muted-foreground">
          {percent(figures.margin_pct_actual_only)}
        </p>
        {figures.margin_actual_only === undefined ? null : (
          <p className="text-sm tabular-nums text-muted-foreground">
            {optionalMoney(figures.margin_actual_only)}
          </p>
        )}
      </div>
    </div>
  );
}

function Figures({ figures }: { figures: ProjectedMarginFigures }) {
  // Contract value and cost are omitted for Aggregate users -- on a single
  // fixed-price project they are that team's pay. No dollars, no list.
  if (
    figures.contract_value === undefined ||
    figures.cost_to_date === undefined ||
    figures.projected_cost === undefined
  ) {
    return null;
  }
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
      <dt className="text-muted-foreground">Contract value</dt>
      <dd className="tabular-nums">{money(figures.contract_value)}</dd>
      <dt className="text-muted-foreground">Cost to date</dt>
      <dd className="tabular-nums">{money(figures.cost_to_date)}</dd>
      <dt className="text-muted-foreground">Projected remaining cost</dt>
      <dd className="tabular-nums">
        {money(figures.projected_cost)}
        {figures.projected_share_of_total_cost_pct != null ? (
          <span className="text-muted-foreground">
            {" "}
            &middot; {percent(figures.projected_share_of_total_cost_pct)} of total cost is projected
          </span>
        ) : null}
      </dd>
    </dl>
  );
}

/** Planned hours the engine could not price, so they are not in the projected cost. */
function hasUnpriced(p: ProjectedMarginComputed): boolean {
  return (p.unpriced_projected_hours ?? 0) > 0;
}

/** Whether the engine said anything that makes the projection less than whole. */
function isPartial(p: ProjectedMarginComputed): boolean {
  return (
    p.remaining_calendar_unknown ||
    p.uncovered_open_periods.length > 0 ||
    hasUnpriced(p) ||
    (p.warnings?.length ?? 0) > 0
  );
}

function unpricedSentence(p: ProjectedMarginComputed): string {
  // The count is an hours figure, so it is absent for Aggregate users.
  const amount =
    p.unpriced_projected_hours === undefined
      ? "Some planned hours"
      : `${hours(p.unpriced_projected_hours)} planned hours`;
  return `${amount} belong to people with no cost rate and are not in the projected cost, so this margin is optimistic.`;
}

/**
 * Why the projection is partial, said once. The engine's warnings when it
 * sent any -- it words every case below itself -- otherwise the same facts
 * from its fields, so an older payload is never flagged without a reason.
 */
function notesFor(p: ProjectedMarginComputed): string[] {
  if ((p.warnings?.length ?? 0) > 0) return p.warnings ?? [];
  const lines: string[] = [];
  if (p.remaining_calendar_unknown) {
    lines.push(
      "This project has no end date, so its remaining calendar is unknown. Cost is projected only for months with planned hours; any months after them are not counted, and the margin is optimistic by whatever they cost.",
    );
  } else if (p.uncovered_open_periods.length > 0) {
    const covered = p.open_periods_remaining.length - p.uncovered_open_periods.length;
    lines.push(
      `Planned hours cover only ${covered} of ${p.open_periods_remaining.length} remaining months. No cost is projected for ${p.uncovered_open_periods.join(", ")}, so this margin is optimistic by whatever those months cost.`,
    );
  }
  if (hasUnpriced(p)) lines.push(unpricedSentence(p));
  return lines;
}

function coverageHint(p: ProjectedMarginComputed): ReactNode {
  return isPartial(p) ? <PartialPlan /> : null;
}

/** The reasons, as quiet lines under the figures, as on the This month card. */
function Notes({ lines }: { lines: { key: string; name?: string; text: string }[] }) {
  if (lines.length === 0) return null;
  return (
    <ul data-testid="projection-warnings" className="space-y-0.5 text-xs text-muted-foreground">
      {lines.map((line) => (
        <li key={line.key}>
          {line.name ? <span className="font-medium">{line.name}:</span> : null}
          {line.name ? " " : null}
          {line.text}
        </li>
      ))}
    </ul>
  );
}

/**
 * How the remaining hours were priced. The sentence is the engine's
 * `cost_rate_basis`, printed as sent: the dashboard does not restate the
 * method, so it cannot drift from it. `cost_rate` is the project's own
 * blended average (booked cost / booked hours), the fallback for anyone with
 * no rate of their own. Aggregate callers get neither, and so no sentence --
 * a guessed one would be the stale one.
 */
function assumption(p: ProjectedMarginComputed): string {
  if (!p.is_projected) {
    return "No open months remain on this project's calendar, so nothing is projected: this is the whole-project margin on booked cost.";
  }
  const parts: string[] = [];
  if (p.cost_rate_basis) parts.push(p.cost_rate_basis);
  if (p.cost_rate != null) {
    parts.push(`This project's own average cost rate so far is ${rate(p.cost_rate)}.`);
  }
  return parts.join(" ");
}

function asOf(value: string | null): string {
  return value ? `Cost booked through ${calendarDay(value)}.` : "";
}

export function ProjectProjectedMarginCard({ projection }: { projection: ProjectProjectedMargin }) {
  const title = "Projected margin (whole project)";
  if (projection.status !== "computed") {
    return (
      <Shell title={title}>
        {projection.status === "excluded" ? (
          <p className="text-sm font-medium">Not projected</p>
        ) : null}
        <p className="text-sm text-muted-foreground">{projection.reason_text}</p>
      </Shell>
    );
  }
  return (
    <Shell title={title}>
      <Headline figures={projection} hint={coverageHint(projection)} />
      <Figures figures={projection} />
      <Notes lines={notesFor(projection).map((text) => ({ key: text, text }))} />
      <p className="text-xs text-muted-foreground">
        {assumption(projection)} {asOf(projection.as_of)} Not included in any closed-period total.
      </p>
    </Shell>
  );
}

/**
 * The engine's basis sentence, from the first included project that projects
 * anything. A project with nothing left to project carries the engine's
 * booked-cost basis instead, which says nothing about remaining hours.
 */
function accountBasis(projection: AccountProjectedMarginComputed): string {
  const basis = projection.projects.find(
    (p) => p.is_projected && p.cost_rate_basis,
  )?.cost_rate_basis;
  return basis ? `${basis} ` : "";
}

type AccountProject = AccountProjectedMarginComputed["projects"][number];

function nameOf(p: AccountProject): string {
  return p.project_name ?? p.demand_id;
}

/**
 * The amber flag on the roll-up: any included project whose projection is
 * partial (months with no plan, planned hours with no cost rate, or an engine
 * warning), or unpriced hours in the totals. Each makes the summed margin
 * optimistic; the reasons are the notes below the table.
 */
function accountHint(projection: AccountProjectedMarginComputed): ReactNode {
  const partial =
    projection.projects_with_uncovered_months > 0 ||
    projection.projects.some(isPartial) ||
    (projection.totals?.unpriced_projected_hours ?? 0) > 0;
  return partial ? <PartialPlan /> : null;
}

/** Each included project's reasons, named by project, printed once. */
function AccountNotes({ projection }: { projection: AccountProjectedMarginComputed }) {
  const lines = projection.projects.flatMap((p) =>
    notesFor(p).map((text) => ({ key: `${p.demand_id}:${text}`, name: nameOf(p), text })),
  );
  return <Notes lines={lines} />;
}

export function AccountProjectedMarginPanel({
  projection,
}: {
  projection: AccountProjectedMargin;
}) {
  const title = "Projected margin (open fixed-price projects)";
  if (projection.status !== "computed") {
    return (
      <Shell title={title}>
        <p className="text-sm text-muted-foreground">{projection.reason_text}</p>
      </Shell>
    );
  }
  const { totals } = projection;
  // Aggregate users get each project's margin percent and nothing in dollars;
  // the two dollar columns go rather than standing empty.
  const showDollars = projection.projects.some((p) => p.contract_value !== undefined);
  return (
    <Shell title={title}>
      <p className="text-sm text-muted-foreground">
        {projection.included} of {projection.fixed_price_projects} open fixed-price projects
        included. Contract value and cost are summed across them before the margin is worked out.
        {projection.completed_not_included > 0
          ? ` ${projection.completed_not_included} completed fixed-price ${
              projection.completed_not_included === 1 ? "project is" : "projects are"
            } not in this roll-up.`
          : ""}
      </p>
      {totals ? (
        <>
          <Headline figures={totals} hint={accountHint(projection)} />
          <Figures figures={totals} />
        </>
      ) : null}
      {projection.projects.length > 0 ? (
        <DataTable
          className="rounded-md"
          headers={[
            { label: "Project" },
            ...(showDollars
              ? [
                  { label: "Contract value", className: "text-right" },
                  { label: "Total cost (projected)", className: "text-right" },
                ]
              : []),
            { label: "Projected margin", className: "text-right" },
            { label: "Months with no plan" },
          ]}
        >
          {projection.projects.map((p) => (
            <tr key={p.demand_id} className="border-b border-border last:border-0">
              <td className="px-4 py-3">
                <Link
                  to="/dashboard/projects/$demandId"
                  params={{ demandId: p.demand_id }}
                  className="hover:underline"
                >
                  {p.project_name ?? p.demand_id}
                </Link>
              </td>
              {showDollars ? (
                <>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {optionalMoney(p.contract_value)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {optionalMoney(p.total_cost)}
                  </td>
                </>
              ) : null}
              <td className="px-4 py-3 text-right tabular-nums">{percent(p.margin_pct)}</td>
              <td className="px-4 py-3 text-sm">
                {p.remaining_calendar_unknown
                  ? "No end date"
                  : p.uncovered_open_periods.length > 0
                    ? p.uncovered_open_periods.join(", ")
                    : "—"}
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
      <AccountNotes projection={projection} />
      {projection.excluded.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">Not projected</p>
          <ul className="space-y-2 text-sm">
            {projection.excluded.map((p) => (
              <li key={p.demand_id}>
                <Link
                  to="/dashboard/projects/$demandId"
                  params={{ demandId: p.demand_id }}
                  className="font-medium hover:underline"
                >
                  {p.project_name ?? p.demand_id}
                </Link>
                <span className="text-muted-foreground"> &mdash; {p.reason_text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {accountBasis(projection)}
        {asOf(projection.as_of)} Not included in any closed-period total.
      </p>
    </Shell>
  );
}
